import type { ReactElement, ReactNode } from 'react';
import { isValidElement, useId, useRef, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Collapse,
  Link,
  Menu,
  MenuItem,
  Popover,
  Skeleton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { type Theme, alpha } from '@mui/material/styles';
import { ChartsContainer } from '@mui/x-charts/ChartsContainer';
import { AreaPlot, LinePlot } from '@mui/x-charts/LineChart';
import { differenceInCalendarDays, formatDistanceToNow } from 'date-fns';
import {
  ChevronDown,
  CircleHelpIcon,
  ExternalLinkIcon,
  PencilIcon,
  TrendingUpIcon,
} from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import type {
  GetOrganisationBillingSummaryQuery,
  GetOrganisationMonitoringQuery,
  GetOrganisationPosthogCsatQuery,
} from '@graphql/__generated__/graphql';
import { TeamAvatar } from '@modules/_shared/components/TeamAvatar';
import {
  COMPACT_USER_AVATAR_SIZE,
  UserAvatar,
} from '@modules/_shared/components/UserAvatar';
import {
  ScoreSignalRow,
  legacyColorToHealthTone,
} from '@modules/organisations/components/ScoreSignalRow';
import { VisibilitySignalPopover } from '@modules/organisations/components/VisibilitySignalPopover';
import { getCloseLeadUrl } from '@modules/organisations/OrganisationMonitoringConnections';
import type { VisibilityScoreResult } from '@modules/organisations/visibilityScorePresenter';
import { useLocale } from '@shared/hooks/locale';
import { customPalette } from '@shared/theme/StyleProvider';

type Organisation = NonNullable<
  GetOrganisationMonitoringQuery['organisations_by_pk']
>;
type BillingSummary = NonNullable<
  GetOrganisationBillingSummaryQuery['organisation_billing_summary']
>;
type CsatSummary = NonNullable<
  GetOrganisationPosthogCsatQuery['organisation_posthog_csat']
>;
type MonitoringTeamUser = NonNullable<
  Organisation['teams'][number]['teams_users'][number]['user']
>;

export type ScoreSignalId =
  | 'profile'
  | 'reviews'
  | 'transactions'
  | 'visibility'
  | 'adoption';

export type ScoreSignalDetail = {
  id: ScoreSignalId;
  label: string;
  value: number;
  color: 'success' | 'warning' | 'error' | 'disabled';
  subtitle?: string;
  tooltip?: string;
};

export type RecentActivityItem = {
  id: string;
  label: string;
  date: Date;
  tone: 'primary' | 'success' | 'warning' | 'secondary';
};

export type EvolutionChart = {
  label: string;
  value: string;
  deltaLabel: string;
  color: string;
  /** One point per month, `label` is the `YYYY-MM` month key. */
  data: { label: string; value: number }[];
};

const dashboardCardSx = {
  height: '100%',
  borderRadius: 2,
  boxShadow: 'none',
  minWidth: 0,
};

const DashboardCardHeader = ({
  title,
  action,
  tooltip,
}: {
  title: string;
  action?: ReactNode;
  tooltip?: string;
}) => (
  <Stack
    direction="row"
    sx={{ justifyContent: 'space-between', alignItems: 'center', gap: 1 }}
  >
    <Stack direction="row" sx={{ alignItems: 'center', gap: 0.5, minWidth: 0 }}>
      <Typography variant="subtitle2" noWrap sx={{ fontWeight: 700 }}>
        {title}
      </Typography>
      {tooltip != null && (
        <Tooltip title={tooltip}>
          <CircleHelpIcon size={16} color={customPalette.gray400} />
        </Tooltip>
      )}
    </Stack>
    {action}
  </Stack>
);

const VisibilityContributionRow = ({
  signal,
  result,
  loading,
}: {
  signal:
    | (VisibilityScoreResult['signals'][number] & {
        id: 'profileViews' | 'valuationWeighted';
      })
    | null;
  result: VisibilityScoreResult | null;
  loading: boolean;
}) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  if (loading || signal == null) {
    return (
      <ScoreSignalRow label="" tone="neutral" percent={0} value="" loading />
    );
  }

  const percent =
    signal.maxPoints > 0
      ? Math.min(100, Math.round((signal.points / signal.maxPoints) * 100))
      : 0;

  const hasPopover = result != null;

  return (
    <>
      <ScoreSignalRow
        label={signal.label}
        tone={legacyColorToHealthTone(signal.color)}
        percent={percent}
        value={`${percent}%`}
        tooltip={hasPopover ? undefined : signal.subtitle}
        onClick={
          hasPopover
            ? event => setAnchor(event.currentTarget as HTMLElement)
            : undefined
        }
      />
      {result != null && (
        <Popover
          open={anchor != null}
          anchorEl={anchor}
          onClose={() => setAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          slotProps={{
            paper: {
              sx: {
                maxWidth: 'min(96vw, 480px)',
                boxShadow: 6,
              },
            },
          }}
        >
          <VisibilitySignalPopover signalId={signal.id} result={result} />
        </Popover>
      )}
    </>
  );
};

const getHealthLabel = (
  score: number,
  t: ReturnType<typeof useLocale>['t'],
) => {
  if (score >= 80) {
    return t('Healthy');
  }
  if (score >= 50) {
    return t('Fair');
  }
  return t('At risk');
};

type ScoreHealthTone = 'healthy' | 'fair' | 'atRisk' | 'neutral';

const getHealthTone = (score: number): ScoreHealthTone => {
  if (score >= 80) {
    return 'healthy';
  }
  if (score >= 50) {
    return 'fair';
  }
  return 'atRisk';
};

const healthToneTextColor: Record<
  ScoreHealthTone,
  'success.dark' | 'warning.dark' | 'error.dark' | 'text.secondary'
> = {
  healthy: 'success.dark',
  fair: 'warning.dark',
  atRisk: 'error.dark',
  neutral: 'text.secondary',
};

const getHealthToneChartColor = (theme: Theme, tone: ScoreHealthTone) => {
  if (tone === 'healthy') {
    return theme.palette.success.main;
  }
  if (tone === 'fair') {
    return theme.palette.warning.main;
  }
  if (tone === 'atRisk') {
    return theme.palette.error.main;
  }
  return theme.palette.text.secondary;
};

const ScoreGaugeCard = ({
  title,
  tooltip,
  score,
  loading,
  subtitle,
  subtitleColor = 'text.secondary',
  action,
  valueLabel,
  onValueClick,
  statusLabel,
  statusTone,
  gaugeStyle = 'conic',
  children,
}: {
  title: string;
  tooltip: string;
  score: number;
  loading: boolean;
  subtitle: string;
  subtitleColor?: 'success.main' | 'error.main' | 'text.secondary';
  action?: ReactNode;
  valueLabel?: ReactNode;
  /** Hidden hook: fired on each click of the gauge number (easter egg). */
  onValueClick?: () => void;
  statusLabel?: string;
  statusTone?: ScoreHealthTone;
  /**
   * `'conic'` (default) renders the flat conic-gradient gauge. `'ring'` renders
   * a doughnut using the same `CircularProgress` style as the signal rows
   * (rounded caps, muted track).
   */
  gaugeStyle?: 'conic' | 'ring';
  children: ReactNode;
}) => {
  const { t } = useLocale();
  const chartValue = loading ? 0 : Math.min(Math.max(score, 0), 100);
  const healthLabel = statusLabel ?? getHealthLabel(score, t);
  const healthTone = statusTone ?? getHealthTone(score);
  const healthColor = healthToneTextColor[healthTone];

  return (
    <Card variant="outlined" sx={dashboardCardSx}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          <DashboardCardHeader
            title={title}
            tooltip={tooltip}
            action={action}
          />
          <Stack direction="row" sx={{ gap: 2, alignItems: 'center' }}>
            {loading ? (
              <Skeleton
                variant="circular"
                sx={{ width: 88, height: 88, flexShrink: 0 }}
              />
            ) : (
              <Box
                sx={{
                  position: 'relative',
                  width: 88,
                  height: 88,
                  flexShrink: 0,
                }}
              >
                {gaugeStyle === 'ring' ? (
                  <>
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={88}
                      thickness={5}
                      sx={{
                        color: 'action.hover',
                        position: 'absolute',
                        inset: 0,
                      }}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={chartValue}
                      size={88}
                      thickness={5}
                      sx={{
                        color: (theme: Theme) =>
                          getHealthToneChartColor(theme, healthTone),
                        position: 'absolute',
                        inset: 0,
                        '& .MuiCircularProgress-circle': {
                          strokeLinecap: 'round',
                        },
                      }}
                    />
                  </>
                ) : (
                  <Box
                    aria-hidden
                    sx={{
                      position: 'absolute',
                      inset: 0,
                      borderRadius: '50%',
                      background: (theme: Theme) =>
                        `radial-gradient(circle, ${
                          theme.palette.background.paper
                        } 0 58%, transparent 59%), conic-gradient(${getHealthToneChartColor(
                          theme,
                          healthTone,
                        )} 0 ${chartValue}%, ${
                          theme.palette.divider
                        } ${chartValue}% 100%)`,
                    }}
                  />
                )}
                <Box
                  onClick={onValueClick}
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    // Keep the label clear of the ring stroke so wide values
                    // like "+100%" never touch the doughnut edge.
                    px: 1.25,
                    // Intentionally no pointer cursor: the click target stays
                    // invisible so it reads as a static label, not a button.
                    userSelect: 'none',
                  }}
                >
                  <Typography
                    component="div"
                    noWrap
                    sx={{
                      fontWeight: 700,
                      lineHeight: 1,
                      textAlign: 'center',
                      // Auto-shrink so longer labels like "+100%" stay
                      // visually centered inside the 88px gauge without the
                      // text clipping or hugging an edge.
                      fontSize: theme => {
                        const labelLength =
                          typeof valueLabel === 'string'
                            ? valueLabel.length
                            : `${score}%`.length;
                        if (labelLength >= 5) {
                          return theme.typography.subtitle1.fontSize;
                        }
                        if (labelLength === 4) {
                          return theme.typography.h6.fontSize;
                        }
                        return theme.typography.h5.fontSize;
                      },
                    }}
                  >
                    {valueLabel ?? `${score}%`}
                  </Typography>
                </Box>
              </Box>
            )}
            <Box sx={{ minWidth: 0 }}>
              {loading ? (
                <Skeleton width={120} height={48} />
              ) : (
                <>
                  <Typography
                    variant="h6"
                    color={healthColor}
                    sx={{ fontWeight: 700, lineHeight: 1.2 }}
                  >
                    {healthLabel}
                  </Typography>
                  <Typography
                    variant="caption"
                    color={subtitleColor}
                    sx={{ fontWeight: 600, display: 'block', mt: 0.5 }}
                  >
                    {subtitle}
                  </Typography>
                </>
              )}
            </Box>
          </Stack>
          {children}
        </Stack>
      </CardContent>
    </Card>
  );
};

const MINI_TREND_CHART_HEIGHT = 64;

const monthKeyToUtcDate = (monthKey: string): Date =>
  new Date(`${monthKey}-01T00:00:00Z`);

const MiniTrendChart = ({
  chart,
  loading,
}: {
  chart: EvolutionChart;
  loading: boolean;
}) => {
  const rawId = useId();
  const gradientId = `mini-trend-gradient-${rawId.replace(/:/g, '')}`;
  const monthDates = chart.data.map(point => monthKeyToUtcDate(point.label));
  const hasData = chart.data.length > 0;
  const deltaValue = Number.parseInt(
    chart.deltaLabel.replace(/[^-\d]/g, ''),
    10,
  );
  const deltaColor =
    chart.deltaLabel === '—'
      ? 'text.secondary'
      : deltaValue > 0
      ? 'success.main'
      : deltaValue < 0
      ? 'error.main'
      : 'text.secondary';

  return (
    <Box
      sx={{
        border: 1,
        borderColor: 'divider',
        borderRadius: 1.5,
        p: 1.5,
        minWidth: 0,
        bgcolor: 'background.paper',
      }}
    >
      <Stack
        direction="row"
        sx={{
          justifyContent: 'space-between',
          alignItems: 'center',
          minHeight: 30,
        }}
      >
        {loading ? (
          <>
            <Skeleton width="55%" height={20} />
            <Skeleton variant="circular" width={24} height={24} />
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" noWrap>
              {chart.label}
            </Typography>
          </>
        )}
      </Stack>
      {loading ? (
        <>
          <Stack
            direction="row"
            sx={{ alignItems: 'baseline', gap: 0.75, my: 0.5, minHeight: 20 }}
          >
            <Skeleton width={52} height={20} />
            <Skeleton width={28} height={12} />
          </Stack>
          <Skeleton
            variant="rounded"
            height={MINI_TREND_CHART_HEIGHT}
            sx={{ borderRadius: 1 }}
          />
        </>
      ) : (
        <>
          <Stack
            direction="row"
            sx={{ alignItems: 'baseline', gap: 0.75, my: 0.5, minHeight: 20 }}
          >
            <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1 }}>
              {chart.value}
            </Typography>
            <Typography
              variant="caption"
              color={deltaColor}
              sx={{ fontWeight: 700 }}
            >
              {chart.deltaLabel}
            </Typography>
          </Stack>
          {hasData ? (
            <ChartsContainer
              height={MINI_TREND_CHART_HEIGHT}
              series={[
                {
                  data: chart.data.map(point => point.value),
                  type: 'line',
                  color: chart.color,
                  curve: 'monotoneX',
                  showMark: false,
                  area: true,
                },
              ]}
              xAxis={[
                {
                  data: monthDates,
                  scaleType: 'utc',
                  hideTooltip: true,
                },
              ]}
              yAxis={[{ min: 0, max: 100 }]}
              margin={{ top: 6, bottom: 6, left: 0, right: 0 }}
              sx={{
                '& .MuiLineChart-area': {
                  fill: `url(#${gradientId})`,
                },
                '& .MuiLineChart-line': {
                  strokeWidth: 2,
                  strokeLinecap: 'round',
                  strokeLinejoin: 'round',
                },
              }}
            >
              <defs>
                <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                  <stop
                    offset="0%"
                    stopColor={chart.color}
                    stopOpacity={0.32}
                  />
                  <stop offset="100%" stopColor={chart.color} stopOpacity={0} />
                </linearGradient>
              </defs>
              <AreaPlot />
              <LinePlot />
            </ChartsContainer>
          ) : (
            <Box
              sx={{
                height: MINI_TREND_CHART_HEIGHT,
                display: 'flex',
                alignItems: 'center',
              }}
            >
              <Typography variant="caption" color="text.secondary">
                —
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

const accountSummaryChipSx = {
  height: 22,
  fontWeight: 600,
  bgcolor: 'background.paper',
  color: 'text.secondary',
  borderColor: 'divider',
  '& .MuiChip-label': {
    px: 0.75,
  },
};

const AccountSummaryChip = ({ label }: { label: ReactNode }) => (
  <Chip
    size="small"
    label={label}
    variant="outlined"
    sx={accountSummaryChipSx}
  />
);

/** Selectable lookback windows for the visibility card, in days. */
export const VISIBILITY_WINDOW_DAYS = [90, 180, 365] as const;
export type VisibilityWindowDays = (typeof VISIBILITY_WINDOW_DAYS)[number];

const useVisibilityWindowLabel = () => {
  const { t } = useLocale();
  return (days: number) =>
    days >= 365 ? t('Last 12 months') : t('Last {{days}} days', { days });
};

// Reuses the shared chip style so the selector font matches every other
// summary chip; only adds the clickable affordance + trailing chevron.
const windowSelectChipSx = {
  ...accountSummaryChipSx,
  cursor: 'pointer',
  transition: 'background-color 120ms ease',
  '&:hover': { bgcolor: 'action.hover' },
} as const;

/** Compact dropdown to switch the visibility card lookback window. */
const VisibilityWindowSelect = ({
  value,
  onChange,
}: {
  value: number;
  onChange: (days: VisibilityWindowDays) => void;
}) => {
  const formatWindow = useVisibilityWindowLabel();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);

  return (
    <>
      <Chip
        size="small"
        variant="outlined"
        clickable
        onClick={event => setAnchor(event.currentTarget)}
        sx={windowSelectChipSx}
        label={
          <Box
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}
          >
            {formatWindow(value)}
            <ChevronDown size={13} style={{ flexShrink: 0, marginRight: -2 }} />
          </Box>
        }
      />
      <Menu
        anchorEl={anchor}
        open={anchor != null}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        {VISIBILITY_WINDOW_DAYS.map(days => (
          <MenuItem
            key={days}
            dense
            selected={days === value}
            onClick={() => {
              onChange(days);
              setAnchor(null);
            }}
          >
            {formatWindow(days)}
          </MenuItem>
        ))}
      </Menu>
    </>
  );
};

const accountSummaryBlockSx = {
  border: 1,
  borderColor: 'divider',
  borderRadius: 2,
  bgcolor: 'background.paper',
  overflow: 'hidden',
  px: 1.25,
  py: 1,
};

const AccountSummaryBlock = ({
  chip,
  loading,
  hero,
  children,
}: {
  chip: ReactNode;
  loading: boolean;
  hero: ReactNode;
  children?: ReactNode;
}) => (
  <Box sx={accountSummaryBlockSx}>
    <Stack direction="row" sx={{ alignItems: 'flex-start', gap: 1 }}>
      <Box sx={{ flex: 1, minWidth: 0 }}>{hero}</Box>
      <Box sx={{ flexShrink: 0 }}>
        {loading ? <Skeleton width={56} height={22} variant="rounded" /> : chip}
      </Box>
    </Stack>
    {children}
  </Box>
);

const AccountSummaryHero = ({
  label,
  value,
  sub,
  loading,
}: {
  label: string;
  value: ReactNode;
  sub?: ReactNode;
  loading: boolean;
}) => (
  <Stack sx={{ gap: 0.25, minWidth: 0 }}>
    <Typography variant="caption" color="text.secondary">
      {label}
    </Typography>
    {loading ? <Skeleton width={120} height={24} /> : value}
    {!loading && sub}
  </Stack>
);

const AccountSummaryMicroRow = ({
  label,
  value,
  loading,
}: {
  label: string;
  value: ReactNode;
  loading: boolean;
}) => (
  <Stack
    direction="row"
    sx={{
      justifyContent: 'space-between',
      alignItems: 'center',
      gap: 1,
      minHeight: 22,
    }}
  >
    <Typography variant="caption" color="text.secondary" noWrap>
      {label}
    </Typography>
    {loading ? (
      <Skeleton width={72} />
    ) : (
      <Box sx={{ textAlign: 'right', minWidth: 0 }}>{value}</Box>
    )}
  </Stack>
);

const getOrganisationLogoUrl = (organisation: Organisation) => {
  const organisationImageUrl =
    organisation.organisation_images?.[0]?.image?.url ?? null;
  if (organisationImageUrl != null) {
    return organisationImageUrl;
  }

  for (const team of organisation.teams) {
    if (team.logo?.url != null) {
      return team.logo.url;
    }
  }

  return null;
};

const accountConnectionChipSx = {
  height: 24,
  fontWeight: 600,
  alignSelf: 'flex-start',
  maxWidth: '100%',
  bgcolor: 'grey.100',
  color: 'text.primary',
  border: 'none',
  '& .MuiChip-label': {
    px: 0.75,
  },
  '& .MuiChip-avatar': {
    ml: 0.5,
  },
};

const AccountConnectionChipField = ({
  label,
  chipLabel,
  loading,
  avatar,
}: {
  label: string;
  chipLabel: string | null | undefined;
  loading: boolean;
  avatar?: ReactElement;
}) => {
  const { t } = useLocale();

  return (
    <Stack
      spacing={0.25}
      sx={{
        minWidth: { xs: 0, sm: 100 },
        flex: { xs: '1 1 45%', sm: '0 0 auto' },
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        noWrap
        sx={{ lineHeight: 1.2 }}
      >
        {label}
      </Typography>
      {loading ? (
        <Skeleton variant="rounded" width={128} height={24} />
      ) : chipLabel != null ? (
        <Chip
          size="small"
          label={chipLabel}
          {...(isValidElement(avatar) ? { avatar } : {})}
          sx={accountConnectionChipSx}
        />
      ) : (
        <Typography
          variant="body2"
          color="text.disabled"
          sx={{ lineHeight: 1.3 }}
        >
          {t('—')}
        </Typography>
      )}
    </Stack>
  );
};

const AccountExternalLinkButton = ({
  label,
  href,
  loading,
}: {
  label: string;
  href: string | null | undefined;
  loading: boolean;
}) => {
  if (loading) {
    return <Skeleton variant="rounded" width={88} height={32} />;
  }

  return (
    <Button
      component={href != null ? 'a' : 'span'}
      href={href ?? undefined}
      target={href != null ? '_blank' : undefined}
      rel={href != null ? 'noopener noreferrer' : undefined}
      variant="outlined"
      size="small"
      disabled={href == null}
      startIcon={<ExternalLinkIcon />}
      onClick={(event: React.MouseEvent) => event.stopPropagation()}
    >
      {label}
    </Button>
  );
};

const getOrganisationUserCount = (organisation: Organisation) =>
  new Set(
    organisation.teams.flatMap(team =>
      team.teams_users
        .map(teamUser => teamUser.user?.id)
        .filter((userId): userId is string => Boolean(userId)),
    ),
  ).size;

export const AccountConnectionsCard = ({
  organisation,
  billingSummary,
  loading,
  formatDate,
}: {
  organisation: Organisation;
  billingSummary: BillingSummary | null | undefined;
  loading: boolean;
  formatDate: (date: string | null | undefined) => string | null;
}) => {
  const { t } = useLocale();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const logoUrl = getOrganisationLogoUrl(organisation);
  const userCount = getOrganisationUserCount(organisation);
  const teamCount = organisation.teams.length;
  const primaryLocality = organisation.teams[0]?.locality;
  const primaryCountry = organisation.teams[0]?.country;
  // "Customer since" reflects the first actual Stripe payment (see
  // organisation_billing_summary), not the CRM record creation date — the
  // latter was misleading, especially in the PL tenant. Omitted until paid.
  const customerSince = formatDate(billingSummary?.customer_since);
  const stripeUrl = billingSummary?.history_url ?? null;
  const closeUrl = organisation.close_lead_id
    ? getCloseLeadUrl(organisation.close_lead_id)
    : null;

  const organisationMeta = [
    t('{{count}} users', { count: userCount }),
    t('{{count}} teams', { count: teamCount }),
    [primaryLocality, primaryCountry].filter(Boolean).join(', '),
    customerSince != null
      ? t('Customer since {{date}}', { date: customerSince })
      : null,
  ]
    .filter(Boolean)
    .join(' · ');

  const openEdit = () => {
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set('edit', '1');
    navigate({ search: nextParams.toString() });
  };

  return (
    <Card variant="outlined" sx={dashboardCardSx}>
      <CardContent sx={{ p: 2 }}>
        <Stack
          direction={{ xs: 'column', lg: 'row' }}
          sx={{
            alignItems: { xs: 'stretch', lg: 'center' },
            gap: { xs: 1.5, lg: 2.5 },
          }}
        >
          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              gap: 1.5,
              minWidth: 0,
              flex: { lg: '1 1 280px' },
            }}
          >
            {loading ? (
              <Skeleton variant="circular" width={50} height={50} />
            ) : (
              <TeamAvatar logoUrl={logoUrl} />
            )}

            <Box sx={{ minWidth: 0, flex: 1 }}>
              {loading ? (
                <>
                  <Skeleton width="70%" height={30} />
                  <Skeleton width="85%" height={18} sx={{ mt: 0.5 }} />
                </>
              ) : (
                <>
                  <Typography
                    variant="h6"
                    noWrap
                    sx={{ fontWeight: 700, lineHeight: 1.2 }}
                  >
                    {organisation.name}
                  </Typography>
                  {organisationMeta && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ lineHeight: 1.35, mt: 0.25 }}
                    >
                      {organisationMeta}
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Stack>

          <Stack
            direction="row"
            sx={{
              flexWrap: 'wrap',
              alignItems: 'flex-end',
              gap: { xs: 1.25, sm: 2, lg: 2.5 },
              flex: { lg: '0 1 auto' },
            }}
          >
            <AccountConnectionChipField
              label={t('Sales Rep')}
              chipLabel={organisation.sales_rep?.full_name}
              loading={loading}
              avatar={
                organisation.sales_rep ? (
                  <UserAvatar
                    user={organisation.sales_rep}
                    size={22}
                    nameFallback={organisation.sales_rep.full_name ?? ''}
                  />
                ) : undefined
              }
            />
            <AccountConnectionChipField
              label={t('CSM')}
              chipLabel={organisation.csm_rep?.full_name}
              loading={loading}
              avatar={
                organisation.csm_rep ? (
                  <UserAvatar
                    user={organisation.csm_rep}
                    size={22}
                    nameFallback={organisation.csm_rep.full_name ?? ''}
                  />
                ) : undefined
              }
            />
            <AccountConnectionChipField
              label={t('Linked tenant')}
              chipLabel={organisation.linked_tenant?.name}
              loading={loading}
            />
          </Stack>

          <Stack
            direction="row"
            sx={{
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              flexShrink: 0,
              ml: { lg: 'auto' },
            }}
          >
            <AccountExternalLinkButton
              label={t('Stripe')}
              href={stripeUrl}
              loading={loading}
            />
            <AccountExternalLinkButton
              label={t('Close')}
              href={closeUrl}
              loading={loading}
            />
            <Link
              component="button"
              type="button"
              underline="none"
              color="primary"
              onClick={openEdit}
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 0.5,
                fontWeight: 600,
                fontSize: theme => theme.typography.body2.fontSize,
                flexShrink: 0,
              }}
            >
              <PencilIcon size={16} />
              {t('Edit')}
            </Link>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export type VisibilityAdminCalibration = {
  sellerContacts: number;
  sellerEnquiries: number;
  phoneClicks: number;
  avgSellerContactsPerMonth: number;
};

export type VisibilityInfo = {
  totalReviews: number;
  transactions24mo: number;
  averagePosition: number | null;
  sizeMultiplier: number;
};

const compactMetricsPanelSx = {
  mt: 0.25,
  px: 1,
  py: 0.75,
  border: 1,
  borderColor: 'divider',
  borderRadius: 1.5,
  bgcolor: 'background.paper',
  // Paint above the admin-calibration drawer so the drawer's top edge is
  // hidden behind this panel — it then reads as sliding out from underneath.
  position: 'relative',
  zIndex: 1,
} as const;

// Distinct grey "drawer" that slides out from underneath the metrics panel
// when the admin-calibration easter egg is unlocked. Full panel width.
// The negative top margin tucks the drawer's top edge up behind the panel
// (which paints on top via its z-index), so only the body emerges below.
const adminCalibrationCardSx = {
  mt: '-14px',
  px: 1,
  pt: '14px',
  pb: 0.75,
  borderRadius: 1.5,
  borderTopLeftRadius: 0,
  borderTopRightRadius: 0,
  border: 1,
  borderColor: 'divider',
  bgcolor: 'grey.100',
} as const;

const CompactMetricRow = ({
  label,
  labelTooltip,
  value,
}: {
  label: string;
  labelTooltip?: string;
  value: ReactNode;
}) => {
  const labelNode = (
    <Typography
      variant="caption"
      color="text.secondary"
      sx={{ lineHeight: 1.3 }}
    >
      {label}
    </Typography>
  );

  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 1,
        minHeight: 18,
      }}
    >
      {labelTooltip != null ? (
        <Tooltip title={labelTooltip}>{labelNode}</Tooltip>
      ) : (
        labelNode
      )}
      <Typography
        variant="caption"
        noWrap
        sx={{ fontWeight: 700, lineHeight: 1.3 }}
      >
        {value}
      </Typography>
    </Stack>
  );
};

const CALIBRATION_REVEAL_STORAGE_KEY =
  'organisation-monitoring:visibility-admin-calibration-revealed';
const RAPID_TAP_WINDOW_MS = 1500;
const RAPID_TAP_COUNT = 3;

const readPersistedReveal = () => {
  try {
    return window.localStorage.getItem(CALIBRATION_REVEAL_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
};

/**
 * Easter egg: tap the gauge number `RAPID_TAP_COUNT` times within
 * `RAPID_TAP_WINDOW_MS` to toggle the hidden admin calibration metrics. The
 * toggle is persisted per browser so it survives reloads, and only ever does
 * anything when the caller is allowed to see the metrics (`enabled`).
 */
const useGaugeTapReveal = ({ enabled }: { enabled: boolean }) => {
  const [revealed, setRevealed] = useState(
    () => enabled && readPersistedReveal(),
  );
  const tapTimestampsRef = useRef<number[]>([]);

  const registerTap = () => {
    if (!enabled) {
      return;
    }
    const now = Date.now();
    const recentTaps = [...tapTimestampsRef.current, now].filter(
      timestamp => now - timestamp <= RAPID_TAP_WINDOW_MS,
    );
    if (recentTaps.length < RAPID_TAP_COUNT) {
      tapTimestampsRef.current = recentTaps;
      return;
    }
    tapTimestampsRef.current = [];
    setRevealed(previous => {
      const next = !previous;
      try {
        window.localStorage.setItem(
          CALIBRATION_REVEAL_STORAGE_KEY,
          next ? '1' : '0',
        );
      } catch {
        // Ignore storage failures (private mode, quota, disabled storage).
      }
      return next;
    });
  };

  return { revealed: enabled && revealed, registerTap };
};

export const VisibilityScoreCard = ({
  score,
  result,
  deltaLabel,
  deltaColor,
  adminCalibration,
  showAdminCalibration,
  info,
  windowDays,
  onWindowDaysChange,
  loading,
}: {
  score: number;
  /** Full visibility result — drives both the signal rows and their popovers. */
  result: VisibilityScoreResult | null;
  deltaLabel: string;
  deltaColor: 'success.main' | 'error.main' | 'text.secondary';
  adminCalibration?: VisibilityAdminCalibration | null;
  showAdminCalibration?: boolean;
  info?: VisibilityInfo | null;
  /** Selected lookback window (days) driving every metric in this card. */
  windowDays: number;
  onWindowDaysChange: (days: VisibilityWindowDays) => void;
  loading: boolean;
}) => {
  const { t } = useLocale();
  const { revealed: calibrationRevealed, registerTap: registerCalibrationTap } =
    useGaugeTapReveal({ enabled: showAdminCalibration === true });

  return (
    <ScoreGaugeCard
      title={t('Visibility score')}
      tooltip={t(
        'Profile views (1 pt each) plus weighted valuation impressions.',
      )}
      score={score}
      gaugeStyle="ring"
      loading={loading}
      subtitle={deltaLabel}
      subtitleColor={deltaColor}
      valueLabel={score > 100 ? '+100%' : `${score}%`}
      onValueClick={showAdminCalibration ? registerCalibrationTap : undefined}
      action={
        <VisibilityWindowSelect
          value={windowDays}
          onChange={onWindowDaysChange}
        />
      }
    >
      <Stack spacing={0.5}>
        {loading || result == null
          ? Array.from({ length: 2 }, (_, index) => (
              <VisibilityContributionRow
                key={index}
                loading
                signal={null}
                result={null}
              />
            ))
          : result.signals.map(signal => (
              <VisibilityContributionRow
                key={signal.id}
                signal={signal}
                result={result}
                loading={false}
              />
            ))}
        {!loading && info != null && (
          <Box sx={compactMetricsPanelSx}>
            <Stack spacing={0.25}>
              <CompactMetricRow
                label={t('Total reviews')}
                value={info.totalReviews}
              />
              <CompactMetricRow
                label={t('Transactions (2 yrs)')}
                value={info.transactions24mo}
              />
              <CompactMetricRow
                label={t('Avg. position')}
                labelTooltip={t(
                  'The average position of your profile in search results on RealAdvisor',
                )}
                value={
                  info.averagePosition == null
                    ? t('—')
                    : info.averagePosition.toFixed(1)
                }
              />
              <CompactMetricRow
                label={t('Size target')}
                labelTooltip={t(
                  'Score target scales with account size (transactions in last 24 months): 0–14 = 1.0x, 15–29 = 1.5x, 30–49 = 2.0x, then +0.1x per 10 transactions.',
                )}
                value={`${info.sizeMultiplier.toFixed(1)}x`}
              />
            </Stack>
          </Box>
        )}
        {!loading && showAdminCalibration && adminCalibration != null && (
          <Collapse in={calibrationRevealed} timeout={280} unmountOnExit>
            <Box sx={adminCalibrationCardSx}>
              <Stack spacing={0.25}>
                <CompactMetricRow
                  label={t('Seller contacts')}
                  labelTooltip={t(
                    'Result-page seller leads attributed to this organisation in the selected window.',
                  )}
                  value={adminCalibration.sellerContacts}
                />
                <CompactMetricRow
                  label={t('Seller enquiries')}
                  labelTooltip={t(
                    'Enquiries (e.g. listing portal contact forms) on properties listed by this organisation in the selected window.',
                  )}
                  value={adminCalibration.sellerEnquiries}
                />
                <CompactMetricRow
                  label={t('Phone clicks')}
                  labelTooltip={t(
                    "Unique users who clicked 'show phone number' on this organisation's agent or agency pages in the selected window.",
                  )}
                  value={adminCalibration.phoneClicks}
                />
                <CompactMetricRow
                  label={t('Avg seller contacts / month')}
                  value={adminCalibration.avgSellerContactsPerMonth.toFixed(1)}
                />
              </Stack>
            </Box>
          </Collapse>
        )}
      </Stack>
    </ScoreGaugeCard>
  );
};

type SubscriptionStatusTone = 'success' | 'warning' | 'neutral';

const SUBSCRIPTION_STATUS_TONE: Record<string, SubscriptionStatusTone> = {
  active: 'success',
  trialing: 'success',
  past_due: 'warning',
  unpaid: 'warning',
  paused: 'warning',
  incomplete: 'warning',
  incomplete_expired: 'neutral',
  canceled: 'neutral',
};

const subscriptionStatusChipSx =
  (tone: SubscriptionStatusTone) => (theme: Theme) => {
    const palette =
      tone === 'success'
        ? theme.palette.success
        : tone === 'warning'
        ? theme.palette.warning
        : null;

    return {
      height: 22,
      fontWeight: 700,
      bgcolor: palette ? alpha(palette.main, 0.14) : theme.palette.action.hover,
      color: palette ? palette.dark : theme.palette.text.secondary,
      '& .MuiChip-label': { px: 0.875 },
    };
  };

/**
 * At-a-glance Stripe subscription status badge. Shows the real Stripe status
 * word, color-coded by tone; `null`/unknown means the org has no subscription.
 */
const SubscriptionStatusBadge = ({
  status,
  loading,
}: {
  status: string | null | undefined;
  loading: boolean;
}) => {
  const { t } = useLocale();

  if (loading) {
    return <Skeleton variant="rounded" width={72} height={22} />;
  }

  const labels: Record<string, string> = {
    active: t('Active'),
    trialing: t('Trial'),
    past_due: t('Past due'),
    unpaid: t('Unpaid'),
    paused: t('Paused'),
    incomplete: t('Incomplete'),
    incomplete_expired: t('Expired'),
    canceled: t('Canceled'),
  };

  const tone: SubscriptionStatusTone =
    status == null ? 'neutral' : SUBSCRIPTION_STATUS_TONE[status] ?? 'neutral';
  const label =
    status == null ? t('No subscription') : labels[status] ?? status;

  return (
    <Chip size="small" label={label} sx={subscriptionStatusChipSx(tone)} />
  );
};

export const BillingAndContractCard = ({
  billingSummary,
  formatMoney,
  formatDate,
  loading,
  billingError,
}: {
  billingSummary: BillingSummary | null | undefined;
  formatMoney: (cents: number, fractionDigits?: number) => string;
  formatDate: (date: string | null | undefined) => string | null;
  loading: boolean;
  billingError?: string;
}) => {
  const { t } = useLocale();

  const renewalDaysLeft =
    billingSummary?.renewal_date == null
      ? null
      : differenceInCalendarDays(
          new Date(billingSummary.renewal_date),
          new Date(),
        );

  const intervalLabel =
    billingSummary?.billing_interval === 'month'
      ? t('Monthly')
      : billingSummary?.billing_interval === 'year'
      ? t('Yearly')
      : billingSummary?.billing_interval === 'mixed'
      ? t('Mixed')
      : null;

  const mrrCents = billingSummary?.mrr_amount_cents ?? 0;
  const listMrrCents = billingSummary?.mrr_list_amount_cents ?? 0;
  const showListMrr = listMrrCents > mrrCents;
  const nextInvoiceAmountLabel =
    billingSummary?.next_invoice_amount_cents != null
      ? formatMoney(billingSummary.next_invoice_amount_cents, 2)
      : t('—');
  const nextInvoiceDateLabel = formatDate(billingSummary?.next_invoice_date);

  const openInvoicesCount = billingSummary?.invoices_outstanding_count ?? 0;
  const openInvoicesAmountCents =
    billingSummary?.invoices_outstanding_amount_cents ?? 0;
  const stripeHistoryUrl = billingSummary?.history_url ?? null;
  const hasOpenInvoices = openInvoicesCount > 0;
  const openInvoicesLabel = `${openInvoicesCount} · ${formatMoney(
    openInvoicesAmountCents,
  )}`;

  return (
    <Card variant="outlined" sx={dashboardCardSx}>
      <CardContent sx={{ p: 0, '&:last-child': { pb: 0 } }}>
        <Box sx={{ px: 2, pt: 2, pb: 1.5 }}>
          <DashboardCardHeader
            title={t('Billing and contract')}
            tooltip={t(
              'Subscription billing, invoices, renewal, and contract dates',
            )}
            action={
              <SubscriptionStatusBadge
                status={billingSummary?.subscription_status}
                loading={loading}
              />
            }
          />
          {billingError != null && (
            <Typography color="warning.main" sx={{ mt: 1 }}>
              {billingError}
            </Typography>
          )}
        </Box>

        <Stack spacing={1.25} sx={{ px: 2, pb: 2 }}>
          <Stack spacing={0.75}>
            <AccountSummaryBlock
              loading={loading}
              chip={<AccountSummaryChip label={intervalLabel ?? t('—')} />}
              hero={
                <AccountSummaryHero
                  label={t('MRR')}
                  loading={loading}
                  value={
                    <Stack
                      direction="row"
                      sx={{
                        alignItems: 'baseline',
                        gap: 0.75,
                        flexWrap: 'wrap',
                      }}
                    >
                      {showListMrr && (
                        <Typography
                          variant="body1"
                          color="text.disabled"
                          sx={{
                            fontWeight: 600,
                            lineHeight: 1.1,
                            textDecoration: 'line-through',
                          }}
                        >
                          {formatMoney(listMrrCents)}
                        </Typography>
                      )}
                      <Typography
                        variant="h6"
                        sx={{ fontWeight: 700, lineHeight: 1.1 }}
                      >
                        {formatMoney(mrrCents)}
                      </Typography>
                      {billingSummary?.discount_label != null && (
                        <AccountSummaryChip
                          label={billingSummary.discount_label}
                        />
                      )}
                    </Stack>
                  }
                />
              }
            >
              <Box sx={{ mt: 0.75 }}>
                <AccountSummaryMicroRow
                  label={t('ARR')}
                  loading={loading}
                  value={
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {formatMoney(billingSummary?.arr_amount_cents ?? 0)}
                    </Typography>
                  }
                />
              </Box>
            </AccountSummaryBlock>

            <AccountSummaryBlock
              loading={loading}
              chip={
                <AccountSummaryChip label={nextInvoiceDateLabel ?? t('—')} />
              }
              hero={
                <AccountSummaryHero
                  label={t('Next invoice')}
                  loading={loading}
                  value={
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 700, lineHeight: 1.1 }}
                    >
                      {nextInvoiceAmountLabel}
                    </Typography>
                  }
                />
              }
            >
              <Box sx={{ mt: 0.75 }}>
                <AccountSummaryMicroRow
                  label={t('Total paid')}
                  loading={loading}
                  value={
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {formatMoney(
                        billingSummary?.lifetime_value_amount_cents ?? 0,
                      )}
                    </Typography>
                  }
                />
                <AccountSummaryMicroRow
                  label={t('Open invoices')}
                  loading={loading}
                  value={
                    !hasOpenInvoices ? (
                      <Typography variant="caption" color="text.secondary">
                        {t('None')}
                      </Typography>
                    ) : stripeHistoryUrl != null ? (
                      <Link
                        href={stripeHistoryUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        underline="hover"
                        color="error.main"
                        sx={{
                          fontWeight: 700,
                          fontSize: theme => theme.typography.caption.fontSize,
                        }}
                        onClick={(event: React.MouseEvent) =>
                          event.stopPropagation()
                        }
                      >
                        {openInvoicesLabel}
                      </Link>
                    ) : (
                      <Typography
                        variant="caption"
                        color="error.main"
                        sx={{ fontWeight: 700 }}
                      >
                        {openInvoicesLabel}
                      </Typography>
                    )
                  }
                />
              </Box>
            </AccountSummaryBlock>

            <AccountSummaryBlock
              loading={loading}
              chip={
                <AccountSummaryChip
                  label={
                    renewalDaysLeft != null && renewalDaysLeft > 0
                      ? t('{{count}} days left', { count: renewalDaysLeft })
                      : t('—')
                  }
                />
              }
              hero={
                <AccountSummaryHero
                  label={t('Renewal')}
                  loading={loading}
                  value={
                    <Typography
                      variant="body1"
                      noWrap
                      sx={{ fontWeight: 600, lineHeight: 1.1 }}
                    >
                      {formatDate(billingSummary?.renewal_date) ?? t('—')}
                    </Typography>
                  }
                />
              }
            >
              <Box sx={{ mt: 0.75 }}>
                <AccountSummaryMicroRow
                  label={t('Contract start')}
                  loading={loading}
                  value={
                    <Typography variant="caption" sx={{ fontWeight: 600 }}>
                      {t('—')}
                    </Typography>
                  }
                />
              </Box>
            </AccountSummaryBlock>
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

const formatCsatScore = (score: number) =>
  Number.isInteger(score) ? String(score) : score.toFixed(1);

export const SentimentCard = ({
  csatSummary,
  teamUsersById,
  loading,
  error,
  formatDate,
}: {
  csatSummary: CsatSummary | null | undefined;
  teamUsersById: Map<string, MonitoringTeamUser>;
  loading: boolean;
  error?: string;
  formatDate: (date: string | null | undefined) => string | null;
}) => {
  const { t } = useLocale();
  const teamUserCount = csatSummary?.team_user_count ?? 0;
  const responseCount = csatSummary?.response_count ?? 0;
  const responseChipLabel = t('{{responded}}/{{total}} responded', {
    responded: responseCount,
    total: teamUserCount,
  });
  const averageScore = csatSummary?.average_score;
  const lastResponseDate = csatSummary?.last_response_date;
  const topResponses = (csatSummary?.responses ?? []).slice(0, 3);
  const remainingResponses = Math.max(
    (csatSummary?.responses?.length ?? 0) - topResponses.length,
    0,
  );
  const scorePercent =
    averageScore == null ? 0 : Math.round((averageScore / 5) * 100);
  const lastAnswerLabel =
    lastResponseDate == null
      ? t('Last answer · {{date}}', { date: t('—') })
      : t('Last answer · {{date}}', {
          date: formatDate(lastResponseDate) ?? t('—'),
        });

  return (
    <ScoreGaugeCard
      title={t('Sentiment score')}
      tooltip={t(
        'CSAT survey responses from organisation team users (last 90 days)',
      )}
      score={scorePercent}
      gaugeStyle="ring"
      loading={loading}
      subtitle={
        averageScore == null
          ? t('No CSAT responses from this team yet')
          : lastAnswerLabel
      }
      action={
        loading ? (
          <Skeleton width={96} height={24} sx={{ borderRadius: 999 }} />
        ) : (
          <AccountSummaryChip label={responseChipLabel} />
        )
      }
      valueLabel={
        averageScore == null ? (
          t('—')
        ) : (
          <>
            {formatCsatScore(averageScore)}
            <Typography
              component="span"
              variant="body2"
              color="text.secondary"
              sx={{ fontWeight: 600 }}
            >
              /5
            </Typography>
          </>
        )
      }
      statusLabel={averageScore == null ? t('No data') : undefined}
      statusTone={averageScore == null ? 'neutral' : undefined}
    >
      <Stack spacing={1}>
        {error != null && (
          <Alert severity="warning" sx={{ py: 0.5 }}>
            {error}
          </Alert>
        )}
        {loading
          ? Array.from({ length: 3 }, (_, index) => (
              <Stack
                key={index}
                direction="row"
                spacing={1}
                sx={{ alignItems: 'center' }}
              >
                <Skeleton
                  variant="circular"
                  width={28}
                  height={28}
                  sx={{ flexShrink: 0 }}
                />
                <Skeleton width="100%" />
                <Skeleton width={32} />
              </Stack>
            ))
          : topResponses.map(response => {
              const user = teamUsersById.get(response.user_id);
              const displayName = user?.full_name?.trim() || t('Unknown user');

              return (
                <Stack
                  key={`${response.user_id}-${response.responded_at}`}
                  direction="row"
                  spacing={1}
                  sx={{ alignItems: 'center', minWidth: 0 }}
                >
                  {user != null ? (
                    <UserAvatar user={user} size={COMPACT_USER_AVATAR_SIZE} />
                  ) : (
                    <Box
                      sx={{
                        width: COMPACT_USER_AVATAR_SIZE,
                        height: COMPACT_USER_AVATAR_SIZE,
                        borderRadius: '50%',
                        bgcolor: 'action.hover',
                        flexShrink: 0,
                      }}
                    />
                  )}
                  <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                    {displayName}
                  </Typography>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontWeight: 700, flexShrink: 0 }}
                  >
                    {formatCsatScore(response.score)}/5
                  </Typography>
                  <Box
                    sx={{
                      position: 'relative',
                      display: 'inline-flex',
                      flexShrink: 0,
                      width: 18,
                      height: 18,
                    }}
                  >
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={18}
                      thickness={8}
                      sx={{
                        color: 'action.hover',
                        position: 'absolute',
                        left: 0,
                      }}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={Math.min(
                        Math.max((response.score / 5) * 100, 0),
                        100,
                      )}
                      size={18}
                      thickness={8}
                      sx={{
                        color: (theme: Theme) =>
                          getHealthToneChartColor(
                            theme,
                            getHealthTone((response.score / 5) * 100),
                          ),
                        position: 'absolute',
                        left: 0,
                        '& .MuiCircularProgress-circle': {
                          strokeLinecap: 'round',
                        },
                      }}
                    />
                  </Box>
                </Stack>
              );
            })}
        {!loading && remainingResponses > 0 && (
          <Typography variant="caption" color="text.secondary">
            {t('+{{count}} more', { count: remainingResponses })}
          </Typography>
        )}
      </Stack>
    </ScoreGaugeCard>
  );
};

const activityToneColor = (tone: RecentActivityItem['tone']) => {
  if (tone === 'success') {
    return 'var(--mui-palette-success-main)';
  }
  if (tone === 'warning') {
    return 'var(--mui-palette-warning-main)';
  }
  if (tone === 'primary') {
    return 'var(--mui-palette-primary-main)';
  }
  return 'var(--mui-palette-secondary-main)';
};

export const ScoreEvolutionCard = ({
  charts,
  loading,
}: {
  charts: EvolutionChart[];
  loading: boolean;
}) => {
  const { t } = useLocale();

  return (
    <Card variant="outlined" sx={dashboardCardSx}>
      <CardContent sx={{ p: 2 }}>
        <Stack spacing={2}>
          <DashboardCardHeader
            title={t('Score evolution')}
            tooltip={t('Trend over the selected monitoring period')}
          />
          <Box
            sx={{
              display: 'grid',
              gap: 1.5,
              gridTemplateColumns: {
                xs: '1fr',
                sm: 'repeat(2, minmax(0, 1fr))',
                lg: 'repeat(3, minmax(0, 1fr))',
              },
            }}
          >
            {loading ? (
              Array.from({ length: 6 }, (_, index) => (
                <MiniTrendChart
                  key={index}
                  loading
                  chart={{
                    label: '',
                    value: '',
                    deltaLabel: '',
                    color: '#1976d2',
                    data: [],
                  }}
                />
              ))
            ) : charts.length === 0 ? (
              <Box
                sx={{
                  gridColumn: { xs: '1', sm: '1 / -1' },
                  py: 3,
                  display: 'flex',
                  justifyContent: 'center',
                }}
              >
                <Typography variant="body2" color="text.secondary">
                  {t('No history yet')}
                </Typography>
              </Box>
            ) : (
              charts.map(chart => (
                <MiniTrendChart
                  key={chart.label}
                  chart={chart}
                  loading={false}
                />
              ))
            )}
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const RecentActivityCard = ({
  items,
  loading,
}: {
  items: RecentActivityItem[];
  loading: boolean;
}) => {
  const { t } = useLocale();

  return (
    <Card variant="outlined" sx={dashboardCardSx}>
      <CardContent sx={{ p: 2, height: '100%' }}>
        <Stack spacing={2} sx={{ height: '100%' }}>
          <DashboardCardHeader
            title={t('Recent activity')}
            tooltip={t('Latest reviews and published transactions')}
          />
          <Stack spacing={1.25} sx={{ flex: 1 }}>
            {loading ? (
              Array.from({ length: 5 }).map((_, index) => (
                <Stack
                  key={index}
                  direction="row"
                  sx={{ justifyContent: 'space-between', gap: 1 }}
                >
                  <Skeleton width="75%" />
                  <Skeleton width={48} />
                </Stack>
              ))
            ) : items.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                {t('No recent activity yet')}
              </Typography>
            ) : (
              items.map(item => (
                <Stack
                  key={item.id}
                  direction="row"
                  sx={{
                    justifyContent: 'space-between',
                    alignItems: 'flex-start',
                    gap: 1,
                  }}
                >
                  <Stack direction="row" sx={{ gap: 1, minWidth: 0 }}>
                    <TrendingUpIcon
                      style={{ marginTop: 2, flexShrink: 0 }}
                      size={18}
                      color={activityToneColor(item.tone)}
                    />
                    <Typography variant="body2" noWrap>
                      {item.label}
                    </Typography>
                  </Stack>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ flexShrink: 0 }}
                  >
                    {formatDistanceToNow(item.date, { addSuffix: true })}
                  </Typography>
                </Stack>
              ))
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};

export const buildRecentActivity = ({
  reviews,
  transactions,
  organisationName,
  t,
}: {
  reviews: Array<{ id?: string | null; review_date?: string | null }>;
  transactions: Array<{ id?: string | null; signature_date?: string | null }>;
  organisationName: string;
  t: ReturnType<typeof useLocale>['t'];
}): RecentActivityItem[] => {
  const items: RecentActivityItem[] = [];

  for (const review of reviews) {
    if (review.review_date == null) {
      continue;
    }
    items.push({
      id: `review-${review.id ?? review.review_date}`,
      label: t('New review received'),
      date: new Date(review.review_date),
      tone: 'success',
    });
  }

  for (const transaction of transactions) {
    if (transaction.signature_date == null) {
      continue;
    }
    items.push({
      id: `transaction-${transaction.id ?? transaction.signature_date}`,
      label: t('Published transaction for {{name}}', {
        name: organisationName,
      }),
      date: new Date(transaction.signature_date),
      tone: 'primary',
    });
  }

  return items.sort((a, b) => b.date.getTime() - a.date.getTime()).slice(0, 5);
};

export const normalizeSeriesToPercent = (
  data: { label: string; value: number }[],
) => {
  const max = Math.max(...data.map(point => point.value), 1);
  return data.map(point => ({
    ...point,
    value: Math.round((point.value / max) * 100),
  }));
};
